import React from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import { T } from "react-translator-component";
import { currencyFormat, i18nValue } from "../../functions";

const quantityControlButtonStyle = {
  width: 34,
  height: 34,
  padding: 0,
  borderRadius: 12,
  border: "none",
  background: "#82378A",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 10px 24px rgba(130,55,138,0.18)",
};

const MenuCommerceSummaryModal = ({
  open,
  onClose,
  cart,
  language,
  currency,
  observations,
  setObservations,
  deliveryPoint,
  setDeliveryPoint,
  askForDeliveryPoint = true,
  deliveryPointEditable = true,
  defaultDeliveryPointCode = "",
  isDeliveryPointMissing = false,
  customizations,
  missingCustomizations,
  hasMissingCustomizations,
  hasCustomizableItems,
  showCartSummary = true,
  onAddItem,
  onRemoveItem,
  onUpdateCustomization,
  onSubmit,
  sending = false,
  submissionState = "idle",
  redirectCountdown = 3,
  onImmediateRedirect,
  submitLabel,
}) => {
  const shouldRenderItems = showCartSummary || hasCustomizableItems;
  const isSubmitting = submissionState === "submitting" || sending;
  const isSuccess = submissionState === "success";
  const canDismiss = !isSubmitting && !isSuccess;
  const handleHide = canDismiss ? onClose : () => {};

  return (
    <Modal
      show={open}
      onHide={handleHide}
      centered
      size="lg"
      backdrop={canDismiss ? true : "static"}
      keyboard={canDismiss}
      contentClassName="border-0"
      dialogClassName="menu-commerce-summary-modal"
    >
      <Modal.Header closeButton={!isSubmitting && !isSuccess} className="menu-commerce-summary-header">
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#82378A" }}>
            {isSubmitting ? T("request.creating-order") : isSuccess ? T("request.order-created") : T("request.your-request")}
          </div>
          <Modal.Title style={{ margin: "4px 0 0", fontWeight: 800, color: "#181C32" }}>
            {isSubmitting
              ? T("request.creating-order-title")
              : isSuccess
                ? T("request.order-created-title")
                : `${cart?.qty || 0} ${cart?.qty === 1 ? T("request.product") : T("request.products")}`}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="menu-commerce-summary-body">
        {isSubmitting ? (
          <div className="menu-commerce-status-screen">
            <div className="menu-commerce-status-orb">
              <div className="menu-commerce-status-spinner" />
            </div>
            <h4 className="menu-commerce-status-title">{T("request.creating-order-title")}</h4>
            <p className="menu-commerce-status-description">{T("request.creating-order-description")}</p>
          </div>
        ) : isSuccess ? (
          <div className="menu-commerce-status-screen">
            <div className="menu-commerce-status-orb is-success">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="menu-commerce-status-check">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h4 className="menu-commerce-status-title">{T("request.order-created-title")}</h4>
            <p className="menu-commerce-status-description">{T("request.order-created-description")}</p>
            <div className="menu-commerce-status-countdown">
              {T("request.redirecting-to-order")} {redirectCountdown}
            </div>
          </div>
        ) : hasMissingCustomizations ? (
          <Alert
            variant="warning"
            style={{ borderRadius: 18, border: "1px solid rgba(255,184,34,0.35)", marginBottom: 18 }}
          >
            {T("request.customize_next")}
          </Alert>
        ) : null}

        {!isSubmitting && !isSuccess && shouldRenderItems
          ? (cart?.items || []).map((item) => (
              <section
                key={item.itemId}
                className="menu-commerce-summary-card"
              >
                <div className="menu-commerce-summary-head">
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 800, color: "#181C32" }}>{item.name}</h5>
                    {item.desc ? (
                      <div style={{ marginTop: 4, color: "#5E6278", fontSize: 13 }}>{item.desc}</div>
                    ) : null}
                  </div>

                  <div className="menu-commerce-summary-total">
                    <div style={{ fontSize: 12, color: "#7E8299" }}>{T("general.total")}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#82378A" }}>
                      {currencyFormat(item.total, currency)}
                    </div>
                  </div>
                </div>

                <div className="menu-commerce-summary-price-row mt-3">
                  <div style={{ fontSize: 13, color: "#7E8299" }}>
                    {currencyFormat(item.price, currency)}
                  </div>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      padding: 4,
                      borderRadius: 16,
                      background: "#F3EAF5",
                    }}
                  >
                    <Button style={quantityControlButtonStyle} onClick={() => onRemoveItem(item.itemId)} disabled={isSubmitting}>
                      <FontAwesomeIcon icon={faMinus} />
                    </Button>
                    <span style={{ minWidth: 18, textAlign: "center", fontWeight: 800, color: "#181C32" }}>{item.qty}</span>
                    <Button style={quantityControlButtonStyle} onClick={() => onAddItem(item.itemId)} disabled={isSubmitting}>
                      <FontAwesomeIcon icon={faPlus} />
                    </Button>
                  </div>
                </div>

                {Object.keys(item.preferences || {}).length > 0
                  ? Array.from({ length: item.qty }).map((_, unitIndex) => (
                      <div
                        key={`${item.itemId}-${unitIndex}`}
                        style={{
                          marginTop: 14,
                          padding: 14,
                          borderRadius: 18,
                          background: "rgba(130,55,138,0.04)",
                          border: "1px solid rgba(130,55,138,0.08)",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 10 }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "6px 10px",
                              borderRadius: 999,
                              background: "#FFFFFF",
                              color: "#82378A",
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            #{unitIndex + 1}
                          </div>

                          <Button
                            variant="light"
                            onClick={() => onRemoveItem(item.itemId, unitIndex)}
                            disabled={isSubmitting}
                            style={{
                              width: 34,
                              height: 34,
                              padding: 0,
                              borderRadius: 12,
                              color: "#D9214E",
                              background: "#FFF5F8",
                              border: "none",
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>

                        {Object.keys(item.preferences || {}).map((preferenceId) => {
                          const preference = item.preferences[preferenceId];
                          const isMissing = Boolean(missingCustomizations?.[item.itemId]?.[unitIndex]?.includes(preferenceId));

                          return (
                            <Form.Group key={preferenceId} className="mb-3">
                              <Form.Label style={{ fontWeight: 700, color: "#181C32" }}>
                                {i18nValue(preference, "title", language)}
                              </Form.Label>
                              <Form.Control
                                as="select"
                                value={customizations?.[item.itemId]?.[unitIndex]?.[preferenceId] || ""}
                                onChange={(event) =>
                                  onUpdateCustomization(item.itemId, unitIndex, preferenceId, event.target.value)
                                }
                                disabled={isSubmitting}
                                style={{
                                  minHeight: 46,
                                  borderRadius: 14,
                                  borderColor: isMissing ? "#D9214E" : "#D9DCE6",
                                  boxShadow: "none",
                                }}
                              >
                                <option value="">{T("select...")}</option>
                                {Object.keys(preference?.options || {}).map((optionId) => (
                                  <option key={optionId} value={optionId}>
                                    {i18nValue(preference.options[optionId], "value", language)}
                                  </option>
                                ))}
                              </Form.Control>
                            </Form.Group>
                          );
                        })}
                      </div>
                    ))
                  : null}
              </section>
            ))
          : null}

        {!isSubmitting && !isSuccess ? (
          <section
            style={{
              padding: 16,
              borderRadius: 22,
              background: "#FFFFFF",
              border: "1px solid rgba(130,55,138,0.12)",
              boxShadow: "0 14px 28px rgba(15,23,42,0.06)",
            }}
          >
            {askForDeliveryPoint ? (
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 700, color: "#181C32" }}>{T("request.delivery-point")}</Form.Label>
                {deliveryPointEditable ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "stretch",
                        gap: 8,
                      }}
                    >
                      {defaultDeliveryPointCode ? (
                        <div
                          style={{
                            minHeight: 46,
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "0 14px",
                            borderRadius: 14,
                            background: "#F3EAF5",
                            color: "#82378A",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {defaultDeliveryPointCode}
                        </div>
                      ) : null}
                      <Form.Control
                        type="text"
                        value={deliveryPoint || ""}
                        onChange={(event) => setDeliveryPoint(event.target.value)}
                        disabled={isSubmitting}
                        isInvalid={isDeliveryPointMissing}
                        style={{ minHeight: 46, borderRadius: 14, boxShadow: "none" }}
                      />
                    </div>
                    {isDeliveryPointMissing ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#D9214E" }}>{T("yup.required")}</div>
                    ) : null}
                  </>
                ) : (
                  <Form.Control
                    type="text"
                    value={defaultDeliveryPointCode || ""}
                    disabled
                    style={{ minHeight: 46, borderRadius: 14, boxShadow: "none", fontWeight: 700 }}
                  />
                )}
              </Form.Group>
            ) : null}

            <Form.Group className="mb-0">
              <Form.Label style={{ fontWeight: 700, color: "#181C32" }}>{T("request.observations")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={observations || ""}
                onChange={(event) => setObservations(event.target.value)}
                placeholder={T("request.observations-placeholder")}
                disabled={isSubmitting}
                style={{ borderRadius: 14, resize: "vertical", boxShadow: "none" }}
              />
            </Form.Group>
          </section>
        ) : null}
      </Modal.Body>

      {isSubmitting ? null : isSuccess ? (
        <Modal.Footer
          className="menu-commerce-summary-footer"
          style={{
            borderTop: "1px solid rgba(130,55,138,0.08)",
          }}
        >
          <div className="menu-commerce-summary-actions" style={{ width: "100%" }}>
            <Button
              onClick={onImmediateRedirect}
              style={{
                width: "100%",
                minHeight: 48,
                borderRadius: 16,
                padding: "0 18px",
                border: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #82378A 0%, #A64AAF 100%)",
                boxShadow: "0 16px 30px rgba(130,55,138,0.24)",
              }}
            >
              {T("request.view-order-now")}
            </Button>
          </div>
        </Modal.Footer>
      ) : (
        <Modal.Footer
          className="menu-commerce-summary-footer"
          style={{
            borderTop: "1px solid rgba(130,55,138,0.08)",
          }}
        >
          <div className="menu-commerce-summary-total">
            <div style={{ fontSize: 12, color: "#7E8299" }}>{T("general.total")}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#82378A" }}>
              {currencyFormat(cart?.total || 0, currency)}
            </div>
          </div>

          <div className="menu-commerce-summary-actions">
            <Button
              variant="light"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                minHeight: 48,
                borderRadius: 16,
                padding: "0 16px",
                border: "none",
                background: "#F3F6F9",
                color: "#5E6278",
                fontWeight: 700,
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              {T("general.cancel")}
            </Button>

            <Button
              onClick={onSubmit}
              disabled={isSubmitting || hasMissingCustomizations || isDeliveryPointMissing}
              style={{
                minHeight: 48,
                borderRadius: 16,
                padding: "0 18px",
                border: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #82378A 0%, #A64AAF 100%)",
                boxShadow: "0 16px 30px rgba(130,55,138,0.24)",
              }}
            >
              {submitLabel || T("general.confirm")}
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default MenuCommerceSummaryModal;
